import React from "react";
import { CalendarDays } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import type {
  LeaveRequestDisplayItem,
  ScheduledListItem,
  ScheduledShift,
} from "../types";
import { groupScheduledShiftsByDay, isSameDayInZone } from "../utils/scheduledShiftRange";
import { ScheduledShiftCard } from "./ScheduledShiftCard";
import { LeaveRequestCard } from "./LeaveRequestCard";

interface ScheduledShiftsListProps {
  shifts: ScheduledListItem[];
  isLoading: boolean;
  error: unknown;
  onStartShift?: (shift: ScheduledShift) => void;
  isStartDisabled?: boolean;
  /**
   * The ID of the currently open (active) actual shift. When a shift's
   * `actual_shift_id` matches this value the card will show as "In Progress"
   * with an End Shift button instead of Start Shift.
   */
  openShiftId?: number | null;
  onEndShift?: (shift: ScheduledShift) => void;
  onEditLeaveRequest?: (request: LeaveRequestDisplayItem) => void;
  onOpenPendingLeaveApproval?: (request: LeaveRequestDisplayItem) => void;
  onViewLeaveRequestDetails?: (request: LeaveRequestDisplayItem) => void;
  highlightedLeaveRequestId?: number | null;
  className?: string;
}

export function ScheduledShiftsList({
  shifts,
  isLoading,
  error,
  onStartShift,
  isStartDisabled,
  openShiftId,
  onEndShift,
  onEditLeaveRequest,
  onOpenPendingLeaveApproval,
  onViewLeaveRequestDetails,
  highlightedLeaveRequestId,
  className,
}: ScheduledShiftsListProps) {
  if (isLoading) {
    return (
      <div
        className={cn("flex items-center justify-center p-8", className)}
        data-testid="scheduled-shifts-list-loading"
      >
        <div className="animate-pulse text-muted-foreground">
          Loading shifts…
        </div>
      </div>
    );
  }

  if (error) {
    const message =
      error instanceof Error ? error.message : "Unknown error";
    return (
      <div
        className={cn("flex items-center justify-center p-8", className)}
        data-testid="scheduled-shifts-list-error"
      >
        <div className="text-destructive text-sm">
          Failed to load shifts: {message}
        </div>
      </div>
    );
  }

  if (shifts.length === 0) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center p-8 text-center",
          className
        )}
        data-testid="scheduled-shifts-list-empty"
      >
        <CalendarDays className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No shifts scheduled for this period</p>
      </div>
    );
  }

  const groups = groupScheduledShiftsByDay(shifts).sort((a, b) => {
    const priorityDelta =
      getGroupPriority(a.shifts, openShiftId) - getGroupPriority(b.shifts, openShiftId);
    if (priorityDelta !== 0) return priorityDelta;
    return a.dayKey.localeCompare(b.dayKey);
  });

  return (
    <div
      className={cn("space-y-6 w-full min-w-0", className)}
      data-testid="scheduled-shifts-list"
    >
      {groups.map((group) => (
        <section
          key={group.dayKey}
          className="min-w-0"
          data-testid={`scheduled-shifts-day-${group.dayKey}`}
        >
          <h4 className="text-xs font-semibold tracking-wide text-gray-500 mb-3 truncate">
            {group.label}
          </h4>
          <div className="space-y-3 min-w-0">
            {group.shifts.map((shift) => (
              shift.kind === "leave" ? (
                <LeaveRequestCard
                  key={`l${shift.leave_request_id}`}
                  request={shift}
                  onEdit={onEditLeaveRequest}
                  onOpenPendingApproval={onOpenPendingLeaveApproval}
                  onViewDetails={onViewLeaveRequestDetails}
                  highlighted={highlightedLeaveRequestId === shift.leave_request_id}
                />
              ) : (
                <ScheduledShiftCard
                  key={shift._unscheduled ? `u${shift.id}` : `s${shift.id}`}
                  shift={shift}
                  onStart={onStartShift}
                  isStartDisabled={isStartDisabled}
                  isOpen={
                    openShiftId != null &&
                    shift.actual_shift_id != null &&
                    shift.actual_shift_id === openShiftId
                  }
                  onEnd={onEndShift}
                />
              )
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function getGroupPriority(
  items: ScheduledListItem[],
  openShiftId?: number | null,
  now: Date = new Date()
): number {
  const hasInProgressShift = items.some(
    (item) =>
      item.kind === "shift" &&
      openShiftId != null &&
      item.actual_shift_id != null &&
      item.actual_shift_id === openShiftId
  );
  if (hasInProgressShift) return 0;

  const hasUpcomingTodayShift = items.some((item) => {
    if (item.kind !== "shift") return false;
    const startMs = Date.parse(item.planned_start_at);
    return (
      !Number.isNaN(startMs) &&
      startMs >= now.getTime() &&
      isSameDayInZone(item.planned_start_at, item.timezone, now)
    );
  });
  return hasUpcomingTodayShift ? 1 : 2;
}
